import { useState, useEffect } from 'react';
import { useMutation, useQueryCache } from "react-query";
import { useCookies } from 'react-cookie';
import Select from 'react-select';
import Loader from './Loader';
import styled from 'styled-components';
import find from 'lodash/find';
import forEach from "lodash/forEach";
import ButtonGroup from './ButtonGroup';
import Button from './Button';
import AddButton from './AddButton';
import { useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import NextText from './NextText';
import Swal from 'sweetalert2';
import { usePlace } from "../api/queries";
import { updateMenuOrder } from "../api/mutations";
import has from 'lodash/has';
import { trackEventSegment } from '../utils/segmentUtils';
import MenuHeader from "./MenuHeader";
import PreviewButton from "./PreviewButton";
import { getPreviewUrl } from "../utils/queryUtils";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import MenuHeading from "./MenuHeading";
import { selectStyles } from "../constants/styleConstants";
import SectionForm from './SectionForm';
import SectionItem from "./SectionItem";


const MenuSelect = styled.div`
  display: flex;
`;


const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    forEach(result, (item, index) => {
        item["order"] = index + 1;
    })

    return result;
};

const MenuItemCreate = (props) => {
  const [cookies] = useCookies(["placeId"]);

  const [loaded, setLoaded] = useState(false);

  const { data } = usePlace(cookies.placeId);

  const [mutate] = useMutation(updateMenuOrder);

  const [sections, setSections] = useState([]);

  const [activeMenu, setActiveMenu] = useState("");

  const cache = useQueryCache();

  const [menus, setMenus] = useState([]);

  let navigate = useNavigate();

  const selectOptions = [];
  menus.map((value, key) => {
    selectOptions.push({ value: value.uuid, label: value.name });
  });

  useEffect(() => {
    if (data && loaded === false) {
      if (data.menus.length > 0) {
        setMenus(data.menus);
        setActiveMenu(data.menus[0]);
      } else {
        Swal.fire({
          title: "Create your menu first",
          text: "Create your first menu before proceeding to this step!",
          confirmButtonText: "Got it",
        });
        navigate("/menu-builder/visual-menu/create-menu");
      }
    } else if (data && loaded === true) {
      setMenus(data.menus);
      const newActiveMenu = find(data.menus, ["uuid", activeMenu.uuid]);
      setActiveMenu(newActiveMenu);
    }
  }, [data, menus]);

  useEffect(() => {
    if (activeMenu) {
      setSections(activeMenu.menu_categories);
      setLoaded(true);
    }
  }, [activeMenu]);

  if (has(data, "menu_categories") && data.menu_categories.length === 0) {
    Swal.fire({
      title: "Create a menu subcategory first",
      text:
        "Create your first menu subcategory before proceeding to this step!",
      confirmButtonText: "Got it",
    });
    navigate("create-categories");
    return <Loader />;
  }

  const handleGoPrev = () => {
    navigate(-1);
  };

  const handleGoNext = () => {
    navigate("/finish");
  };

  const onChange = (value, action) => {
    if (action.action === "select-option") {
      const menu = find(menus, ["uuid", value.value]);
      setActiveMenu(menu);
    }
  };

  const onDragEnd = (result) => {
    if (
      !result.destination ||
      result.destination.index === result.source.index
    ) {
      return;
    }
    const items = reorder(
      sections,
      result.source.index,
      result.destination.index
    );
    setLoaded(false);
    mutate(
      {
        menuId: activeMenu.uuid,
        values: {
          menu_categories: items,
        },
      },
      {
        onSuccess: () => {
          trackEventSegment("Reordered sections", {
            subCategoryName: activeMenu.name,
            subCategoryUuid: activeMenu.uuid,
          });
        },
        onSettled: () => {
          cache.invalidateQueries("placeDetail");
        },
      }
    );
  };

  const sectionList = () => {
    return (
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="list" isDropDisabled={false}>
          {(provided, snapshot) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {sections.map((item, index) => (
                <Draggable
                  isDragDisabled={false}
                  key={item.uuid}
                  draggableId={item.uuid}
                  index={index}
                >
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.draggableProps}>
                      <span {...provided.dragHandleProps}></span>
                      <SectionItem
                        numOfSections={sections.length}
                        dragHandle={provided.dragHandleProps}
                        sectionId={item.uuid}
                        menuId={activeMenu.uuid}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  };

  return (
    <>
      {loaded ? (
        <>
          <MenuHeader>
            <MenuSelect>
              <Select
                styles={selectStyles}
                options={selectOptions}
                onChange={onChange}
                defaultValue={selectOptions[0]}
              />
            </MenuSelect>
            <PreviewButton title="Preview" icon={faEye} small={true} href={getPreviewUrl(cookies.placeId)} />
          </MenuHeader>
          {sections.length > 0 ? sectionList() : <p> No menu sections yet! </p>}
          <SectionForm
            menuId={activeMenu.uuid}
            numOfSections={sections.length}
            element={<AddButton>Add menu section</AddButton>}
          />
          <ButtonGroup halfWidth={true}>
            <Button type="button" onClick={() => handleGoPrev()} inversed={true} text="Back" />
            <Button type="submit" text="Continue" onClick={() => handleGoNext()} />
          </ButtonGroup>
        </>
      ) : (
        <Loader />
      )}
      <NextText>Next step: Submit for approval</NextText>
    </>
  );
}

export default MenuItemCreate;
